import clsx from 'clsx';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { IHeaderTab } from '@/domain/core/HeaderStore';
import { ScrollArea, ScrollBar } from '@/ui/ScrollArea';

interface HeaderMenuProps {
	menuItems: IHeaderTab[];
	orientation?: 'horizontal' | 'vertical';
}

const HeaderMenu = (props: HeaderMenuProps) => {
	const { menuItems, orientation } = props;
	const { pathname, asPath } = useRouter();
	const { t } = useTranslation();
	const horizontalViewportRef = React.useRef<HTMLDivElement>(null);

	const isCurrentTabActive = (item: IHeaderTab) => {
        if (item.activeRegexp) {
            return item.activeRegexp.test(asPath);
        }
        return pathname.startsWith(item.path);
    }
	const handleHorizontalWheel = React.useCallback((event: WheelEvent) => {
		const viewport = horizontalViewportRef.current;

		if (!viewport) {
			return;
		}

		const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth;

		if (maxScrollLeft <= 0) {
			return;
		}

		const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;

		if (!delta) {
			return;
		}

		const nextScrollLeft = Math.min(Math.max(viewport.scrollLeft + delta, 0), maxScrollLeft);

		if (nextScrollLeft === viewport.scrollLeft) {
			return;
		}

		event.preventDefault();
		viewport.scrollLeft = nextScrollLeft;
	}, []);

	React.useEffect(() => {
		if (orientation !== 'horizontal') {
			return undefined;
		}

		const viewport = horizontalViewportRef.current;

		if (!viewport) {
			return undefined;
		}

		viewport.addEventListener('wheel', handleHorizontalWheel, { passive: false });

		return () => {
			viewport.removeEventListener('wheel', handleHorizontalWheel);
		};
	}, [handleHorizontalWheel, orientation]);

	return (
		<>
			{orientation === 'horizontal' && (
				<nav
					role="navigation"
					aria-label={t('accessibility.menu')}
					className="flex min-w-0 gap-2 flex-grow overflow-x-hidden">
					<ScrollArea
						className="whitespace-nowrap"
						viewportRef={horizontalViewportRef}
					>
						<div className="flex w-max space-x-4">
							{menuItems
								.filter((item) => item.visible ?? true)
								.map((item) => (
									<Link href={item.path} key={item.name}>
										<a
											className={clsx(
												'px-4 py-6 text-base whitespace-nowrap',
												isCurrentTabActive(item)
													? 'text-blue-500'
													: 'text-gray-700',
											)}
										>
											{t(item.name)}
										</a>
									</Link>
								))}
						</div>
						<ScrollBar orientation="horizontal" />
					</ScrollArea>
				</nav>
			)}

			{orientation === 'vertical' && (
				<nav
					role="navigation"
					aria-label={t('accessibility.menu')}
					className="flex flex-col gap-2 flex-grow overflow-y-hidden">
					<ScrollArea className="whitespace-nowrap">
						<div className="flex flex-col w-max">
							{menuItems
								.filter((item) => item.visible ?? true)
								.map((item) => (
									<Link href={item.path} key={item.name}>
										<a
											className={clsx(
												'px-4 py-6 text-sm whitespace-nowrap',
												isCurrentTabActive(item)
													? 'text-blue-500'
													: 'text-gray-700',
											)}
											aria-current={isCurrentTabActive(item) ? 'page' : undefined}
										>
											{t(item.name)}
										</a>
									</Link>
								))}
						</div>
						<ScrollBar orientation="vertical" />
					</ScrollArea>
				</nav>
			)}
		</>
	);
};

export { HeaderMenu };
